/** Aggregate tree stats — derived from zustand store, no SQL queries */
import { useMemo } from "react";
import { useDashboardStore, type StreamStats } from "./store";
import type { TreeNode } from "./tree";

export interface NodeStats {
    totalSize: number;
    totalDuration: number;
    totalStored: number;
    totalChunks: number;
    pct: number;
}

export function useTreeStats(tree: TreeNode[]) {
    const allStats = useDashboardStore(s => s.stats);

    return useMemo(() => {
        const statsMap = new Map<string, NodeStats>();
        if (!tree.length) return statsMap;

        function streamTotal(s: StreamStats): number {
            return s.pending + s.downloading + s.stored + s.failed + s.expired;
        }

        function rollUp(node: TreeNode): NodeStats {
            let totalSize = 0, totalDuration = 0, totalStored = 0, totalChunks = 0;

            if (node.streams) {
                for (const s of node.streams) {
                    // Include video stats
                    const vs = allStats[s.stream_key];
                    if (vs) {
                        totalSize += vs.totalSize;
                        totalDuration += vs.totalDuration;
                        totalStored += vs.stored;
                        totalChunks += streamTotal(vs);
                    }
                    // Include audio stats
                    const as = allStats[s.stream_key + "-audio"];
                    if (as) {
                        totalSize += as.totalSize;
                        totalStored += as.stored;
                        totalChunks += streamTotal(as);
                    }
                }
            }

            if (node.children.length > 0) {
                totalSize = 0; totalDuration = 0; totalStored = 0; totalChunks = 0;
                for (const child of node.children) {
                    const childStats = rollUp(child);
                    totalSize += childStats.totalSize;
                    totalDuration += childStats.totalDuration;
                    totalStored += childStats.totalStored;
                    totalChunks += childStats.totalChunks;
                }
            }

            const pct = totalChunks > 0 ? Math.round(totalStored / totalChunks * 100) : 0;
            const stats: NodeStats = { totalSize, totalDuration, totalStored, totalChunks, pct };
            statsMap.set(node.key, stats);
            return stats;
        }

        for (const yearNode of tree) rollUp(yearNode);
        return statsMap;
    }, [allStats, tree]);
}
